/**
 * Feedback Buttons Component
 * Thumbs up/down UI for marking findings as correct or incorrect
 */

import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown, X } from 'lucide-react';
import type { FeedbackType } from '@/types/feedback';
import { useFeedbackStore } from '@/store/feedbackStore';

interface FeedbackButtonsProps {
  findingId: string;
  ruleId: string;
  elementId: number;
  compact?: boolean;
  showOnHover?: boolean;
}

export const FeedbackButtons: React.FC<FeedbackButtonsProps> = ({
  findingId,
  ruleId,
  elementId,
  compact = false,
  showOnHover = true,
}) => {
  const { getFeedback, setFeedback, removeFeedback } = useFeedbackStore();
  const [showNotes, setShowNotes] = useState(false);
  const [notes, setNotes] = useState('');

  const currentFeedback = getFeedback(findingId);
  const activeFeedback = currentFeedback?.feedback;

  const handleFeedback = (feedback: FeedbackType) => {
    if (feedback === activeFeedback) {
      // Toggle off if clicking the same button
      removeFeedback(findingId);
      setShowNotes(false);
      setNotes('');
    } else if (feedback === 'incorrect') {
      // Show notes dialog for incorrect feedback
      setShowNotes(true);
    } else {
      setFeedback(findingId, ruleId, elementId, feedback);
      setShowNotes(false);
    }
  };

  const handleSaveNotes = () => {
    setFeedback(findingId, ruleId, elementId, 'incorrect', notes);
    setShowNotes(false);
    setNotes('');
  };

  const buttonClass = (feedbackType: FeedbackType) => {
    const isActive = activeFeedback === feedbackType;
    const baseClass = compact
      ? 'p-1 rounded transition-all'
      : 'p-1.5 rounded-md transition-all';

    if (isActive) {
      return feedbackType === 'correct'
        ? `${baseClass} bg-green-500 text-white hover:bg-green-600`
        : `${baseClass} bg-red-500 text-black hover:bg-red-600`;
    }

    return `${baseClass} text-gray-400 hover:text-gray-600 hover:bg-gray-50`;
  };

  const iconSize = compact ? 14 : 16;

  return (
    <>
      <div
        className={`flex items-center gap-0.5 ${
          showOnHover ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'
        } transition-opacity`}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleFeedback('correct');
          }}
          className={buttonClass('correct')}
          title="Mark as correct"
          aria-label="Mark as correct"
        >
          <ThumbsUp size={iconSize} />
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            handleFeedback('incorrect');
          }}
          className={buttonClass('incorrect')}
          title="Mark as incorrect"
          aria-label="Mark as incorrect"
        >
          <ThumbsDown size={iconSize} />
        </button>
      </div>

      {/* Notes Dialog */}
      {showNotes && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={(e) => {
            e.stopPropagation();
            setShowNotes(false);
          }}
        >
          <div
            className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl border border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">
                Why is this incorrect?
              </h3>
              <button
                onClick={() => setShowNotes(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional: Add notes about why this finding is incorrect..."
              className="w-full h-24 px-3 py-2 bg-gray-900 border border-gray-600 rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              autoFocus
            />

            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowNotes(false)}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNotes}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md transition-colors"
              >
                Mark as Incorrect
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
